import { apiService } from './api';
import {
  Batch,
  CreateBatch,
  UpdateBatch,
  BatchListRequest,
  BatchListResponse,
  BatchSampleLink,
  CreateBatchSampleLink,
  // Batch import types
  BatchStatus,
  BatchWithStatus,
  CreateBatchForImportDto,
  BatchStatusUpdateDto,
  BatchFilterOptions,
  UnlinkedBatch, ProcessingAlertsResponse } from '../types/batch';
import { BatchAnalysisResponse } from '../types/plantPerformance';

export const batchService = {
  // Get batches with pagination and filtering
  getBatches: async (params: BatchListRequest = {}) => {
    return apiService.get<BatchListResponse>('/batch', params);
  },

  // Get distinct filter-dropdown values (farmers/farms/crops/agents) for the Batches list.
  // Replaces the old "prefetch 1000 batches on mount and dedup client-side" pattern.
  getFilterOptions: async () => {
    return apiService.get<BatchFilterOptions>('/batch/filter-options');
  },

  // Get batch by ID
  getById: async (id: number) => {
    return apiService.get<Batch>(`/batch/${id}`);
  },

  // Create a new batch
  create: async (batch: CreateBatch) => {
    return apiService.post<Batch>('/batch', batch);
  },

  // Update an existing batch
  update: async (id: number, batch: UpdateBatch) => {
    return apiService.put<Batch>(`/batch/${id}`, batch);
  },

  // Delete a batch
  delete: async (id: number, deleteAssociatedSamples: boolean = false) => {
    return apiService.delete<void>(`/batch/${id}?deleteAssociatedSamples=${deleteAssociatedSamples}`);
  },

  // Get batch by batch number
  getByBatchNumber: async (batchNumber: string) => {
    return apiService.get<Batch>(`/batch/by-number/${encodeURIComponent(batchNumber)}`);
  },

  // Get sample links for a batch
  getBatchSampleLinks: async (id: number) => {
    return apiService.get<BatchSampleLink[]>(`/batch/${id}/sample-links`);
  },

  // Add a sample link to a batch
  addSampleLink: async (id: number, linkDto: CreateBatchSampleLink) => {
    return apiService.post<Batch>(`/batch/${id}/sample-links`, linkDto);
  },

  // Remove a sample link from a batch
  removeSampleLink: async (id: number, linkId: number) => {
    return apiService.delete<void>(`/batch/${id}/sample-links/${linkId}`);
  },

  // Get all active batches for dropdowns
  getActiveBatches: async () => {
    return apiService.get<Batch[]>('/batch/active');
  },

  // Get batches not linked to any workflow (for ExistingBatch flow). Returns the slim UnlinkedBatch
  // shape (mini farm/farmer/lab navs + sampleCount + resultTypes) — no nested sampleLinks[] graph.
  getUnlinkedBatches: async () => {
    return apiService.get<UnlinkedBatch[]>('/batch/unlinked');
  },

  // Create batch from files with automatic metadata resolution
  createFromFiles: async (files: Array<{fileName: string; resultType: string}>) => {
    return apiService.post<Batch>('/batch/create-from-files', { files });
  },

  // Get batch analysis results based on linked samples
  getBatchAnalysis: async (id: number) => {
    return apiService.get<BatchAnalysisResponse>(`/batch/${id}/analysis`);
  },

  // ============================================================================
  // BATCH IMPORT METHODS - For dual-file batch import functionality
  // ============================================================================

  // Create a batch for import workflow - simplified batch creation
  createForImport: async (createDto: CreateBatchForImportDto) => {
    return apiService.post<BatchWithStatus>('/batch/create-for-import', createDto);
  },

  // Update batch status during import processing
  updateStatus: async (id: number, statusDto: BatchStatusUpdateDto) => {
    return apiService.put<BatchWithStatus>(`/batch/${id}/status`, statusDto);
  },

  // Get batch with status information
  getByIdWithStatus: async (id: number) => {
    return apiService.get<BatchWithStatus>(`/batch/${id}`);
  },

  // Get batches filtered by status
  getBatchesByStatus: async (status: BatchStatus, params: BatchListRequest = {}) => {
    return apiService.get<BatchListResponse>('/batch', { ...params, status });
  },

  // Check if batch can be processed (all conditions met)
  checkBatchReadiness: async (id: number) => {
    return apiService.get<{
      isReady: boolean;
      missingRequirements: string[];
      recommendedActions: string[];
    }>(`/batch/${id}/readiness`);
  },

  // Generate a new batch reference number
  generateBatchReference: async (farmId: number) => {
    return apiService.get<{ batchNumber: string }>(`/batch/generate/${farmId}/0`);
  },

  // ============================================================================
  // BATCH IMPORT API METHODS - Direct integration with BatchImportController
  // ============================================================================

  // Upload dual files for batch import - simplified endpoint
  uploadDualFiles: async (
    sapFile: File,
    tissueFile: File
  ) => {
    const formData = new FormData();
    formData.append('sapFile', sapFile);
    formData.append('tissueFile', tissueFile);

    return apiService.post<{
      commonMetadata: unknown;
      sapFilePreview: unknown;
      tissueFilePreview: unknown;
      crossFileValidation: unknown[];
      isValid: boolean;
      createdAt: string;
    }>('/batchimport/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Process batch import with resolved samples
  processBatchImport: async (request: {
    sapImportId: string;
    tissueImportId: string;
    sapSamples: unknown[];
    tissueSamples: unknown[];
    cropResolution: {
      sap: {
        cropTypeId: number;
        cropSubTypeId: number;
        farmPlotId: number;
      };
      tissue: {
        cropTypeId: number;
        cropSubTypeId: number;
        farmPlotId: number;
      };
    };
  }) => {
    return apiService.post<{
      batchId: string;
      status: string;
      processingStartedAt: string;
    }>('/batchimport/process', request);
  },

  // Get batch processing status
  getBatchProcessingStatus: async (batchId: string) => {
    return apiService.get<{
      batchId: string;
      status: 'Created' | 'Processing' | 'Completed' | 'Failed';
      sapProgress?: number;
      tissueProgress?: number;
      overallProgress?: number;
      error?: string;
      createdAt: string;
      completedAt?: string;
    }>(`/batchimport/status/${batchId}`);
  },

  // Get latest PDF report for batch
  getLatestPdf: async (id: number) => {
    return apiService.get<{ id: number; status: string; filePath: string }>(`/batch/${id}/latest-pdf`);
  },

  // Get processing alerts for a batch (missing hybrid norms, failed reports)
  getProcessingAlerts: async (id: number) => {
    return apiService.get<ProcessingAlertsResponse>(
      `/batch/${id}/processing-alerts`
    );
  }
};